import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildArticleJsonLd } from '@/lib/seo';
import { kaspreadsheetUrl } from '@/mocks/categories';

export default function BaseTaoFormulas() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <JsonLd data={buildArticleJsonLd({ headline: 'BaseTao Spreadsheet Formulas Guide: Essential Functions for Power Users', description: 'Master the most powerful BaseTao Spreadsheet formulas, from basic calculations to advanced automation logic, with practical examples.', path: '/basetao-spreadsheet-formulas' })} />
      <Navbar />
      <main className="pt-20">
        <article>
          <header className="bg-background-100 py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-foreground-700">Formulas</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                BaseTao Spreadsheet Formulas Guide: Essential Functions for Power Users
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed mb-6">
                Master the most powerful formulas and functions in BaseTao Spreadsheet. From basic calculations to advanced automation logic, this guide covers every formula category with practical examples for product sourcing workflows.
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span>Updated: August 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>15 min read</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="space-y-10 text-foreground-700">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Understanding the BaseTao Spreadsheet Formula Engine</h2>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  The formula engine in BaseTao Spreadsheet supports over two hundred functions spanning mathematical calculations, text manipulation, date arithmetic, conditional logic, and data aggregation. Unlike generic spreadsheet formulas that require extensive manual setup, BaseTao formulas can directly reference live product data — current prices, supplier ratings, shipping estimates, and inventory levels — without needing to manually import or update values. This tight integration with the platform&apos;s data layer makes formulas both more powerful and easier to use than their counterparts in traditional spreadsheet applications.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Essential Formula Categories for Sourcing</h2>
                <div className="space-y-5">
                  {[
                    { title: 'Cost Calculation Formulas', desc: 'Calculate total landed cost, per-unit cost including shipping, customs duty estimates, and profit margins. The TOTALCOST() function automatically aggregates product price, shipping estimates, and applicable fees into a single value, while MARGIN() calculates potential profit based on your target selling price.' },
                    { title: 'Price Monitoring Formulas', desc: 'Set up automatic price alerts using PRICEALERT() and TRACKCHANGE() functions. Configure thresholds that trigger notifications when products drop below target prices or when significant price changes occur within specified time periods.' },
                    { title: 'Shipping Optimization Formulas', desc: 'Compare shipping costs across carriers with SHIPCOMPARE() and find the optimal consolidation strategy using CONSOLIDATE(). These functions analyze your entire product list and recommend the most cost-effective shipping approach.' },
                    { title: 'Conditional Logic Formulas', desc: 'Implement decision automation with IF(), SWITCH(), and CASEWHEN() functions. Automatically categorize products, assign priority levels, and route items to appropriate action queues based on configurable business rules.' },
                  ].map((item) => (
                    <div key={item.title} className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Practical Formula Examples</h2>
                <div className="bg-foreground-50 rounded-lg p-5 border border-background-200/60 font-mono text-xs md:text-sm space-y-3">
                  <div>
                    <span className="text-foreground-500">Total Cost Calculation:</span>
                    <p className="text-primary-700 mt-1">TOTALCOST(product_price, shipping_estimate, customs_rate, 0.05)</p>
                  </div>
                  <div>
                    <span className="text-foreground-500">Price Drop Alert:</span>
                    <p className="text-primary-700 mt-1">PRICEALERT(current_price, target_price, &quot;email&quot;, 10)</p>
                  </div>
                  <div>
                    <span className="text-foreground-500">Shipping Comparison:</span>
                    <p className="text-primary-700 mt-1">SHIPCOMPARE(weight, destination, [&quot;standard&quot;, &quot;express&quot;, &quot;premium&quot;])</p>
                  </div>
                </div>
              </section>

              <section className="bg-accent-50 rounded-lg p-6 md:p-8 border border-accent-100">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-3">Learn More About BaseTao Spreadsheet</h2>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <Link to="/" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    Explore More Guides
                  </Link>
                  <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                    Visit BaseTao Spreadsheet
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm"></i></div>
                  </a>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}